import type {
  DiseaseTaxonomyNode,
  DiseaseTaxonomyRoot,
  CachedEntry,
} from "../types/diseaseTaxonomy.types";

const DB_NAME = "diseaseTaxonomyDB";
const DB_VERSION = 1;
const STORE_NAME = "taxonomy";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const KEY_ROOTS = "roots";
const keyForTaxonomy = (rootMondoId: string) => `taxonomy_${rootMondoId}`;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getItem<T>(key: string): Promise<CachedEntry<T> | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as CachedEntry<T> | undefined);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function setItem<T>(key: string, data: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const entry: CachedEntry<T> = { data, timestamp: Date.now() };
    const req = store.put(entry, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

function isFresh<T>(entry: CachedEntry<T> | undefined): entry is CachedEntry<T> {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
}

// ── Public API ──────────────────────────────────────────────────

export async function getCachedRoots(): Promise<DiseaseTaxonomyRoot[] | null> {
  try {
    const entry = await getItem<DiseaseTaxonomyRoot[]>(KEY_ROOTS);
    return isFresh(entry) ? entry.data : null;
  } catch {
    return null;
  }
}

export async function setCachedRoots(data: DiseaseTaxonomyRoot[]): Promise<void> {
  try {
    await setItem(KEY_ROOTS, data);
  } catch {
    /* IndexedDB write failures are non-fatal */
  }
}

export async function getCachedTaxonomy(
  rootMondoId: string
): Promise<DiseaseTaxonomyNode[] | null> {
  try {
    const entry = await getItem<DiseaseTaxonomyNode[]>(keyForTaxonomy(rootMondoId));
    return isFresh(entry) ? entry.data : null;
  } catch {
    return null;
  }
}

export async function setCachedTaxonomy(
  rootMondoId: string,
  data: DiseaseTaxonomyNode[]
): Promise<void> {
  try {
    await setItem(keyForTaxonomy(rootMondoId), data);
  } catch {
    /* non-fatal */
  }
}
